import { msg } from "@lingui/core/macro";
import { i18n } from "@lingui/core";

export type SpotsLeftScarcityLabel =
  | { kind: "text"; label: string }
  | { kind: "range"; min: number; label: string }
  | { kind: "exact"; remaining: number; label: string };

export function getSpotsLeftScarcityLabel(
  capacityInput: number | null | undefined,
  registeredInput: number | null | undefined,
  availableInput: number | null | undefined,
): SpotsLeftScarcityLabel {
  const capacity =
    typeof capacityInput === "number" && isFinite(capacityInput)
      ? capacityInput
      : 0;
  const registered =
    typeof registeredInput === "number" && isFinite(registeredInput)
      ? registeredInput
      : 0;
  const available =
    typeof availableInput === "number" && isFinite(availableInput)
      ? Math.max(availableInput, 0)
      : null;

  const derivedRemaining = Math.max(capacity - registered, 0);
  const remaining = available ?? derivedRemaining;

  if (!capacity || capacity <= 0) {
    // No capacity cap (unlimited) → show nothing
    return { kind: "text", label: "" };
  }

  const pctRemaining = derivedRemaining / capacity;

  // 1) Very early: avoid big exact numbers
  if (pctRemaining >= 0.8) {
    return { kind: "text", label: i18n._(msg`Limited to ${capacity} spots`) };
  }

  // 2) Bucketed ranges to reduce abundance perception
  if (remaining >= 80)
    return { kind: "range", min: 80, label: i18n._(msg`80+ spots left`) };
  if (remaining >= 60)
    return { kind: "range", min: 60, label: i18n._(msg`60+ spots left`) };
  if (remaining >= 40)
    return { kind: "range", min: 40, label: i18n._(msg`40+ spots left`) };
  if (remaining >= 30)
    return { kind: "range", min: 30, label: i18n._(msg`30+ spots left`) };
  if (remaining >= 20)
    return { kind: "range", min: 20, label: i18n._(msg`20+ spots left`) };

  // 3) Escalate urgency as we get low
  if (remaining >= 10)
    return { kind: "text", label: i18n._(msg`Less than 20 spots left`) };

  // 4) Final scarcity (return the exact number of spots left)
  if (remaining === 1) {
    return { kind: "exact", remaining, label: i18n._(msg`Only 1 spot left`) };
  }
  if (remaining === 0) {
    return { kind: "text", label: i18n._(msg`No spots left!`) };
  }
  return { kind: "exact", remaining, label: i18n._(msg`Only ${remaining} spots left`) };
}
